import type { Point, Sprite } from 'pixi.js';
import { Container, Rectangle } from 'pixi.js';
import { Signal } from 'typed-signals';
import { GameSceneLayout } from '../../../layouts/GameSceneLayout';
import type { AppStateController } from '../../app/state/AppStateController';
import { registerResizableObject } from '../../utils/pixi/registerResizableObject';
import { applyLayoutState, revokeLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';
import type { GameAreaView } from '../boardMVC/GameAreaView';
import type { BoardView } from '../boardMVC/views/board/BoardView';
import { BoosterUseOverlayView } from '../boosters/ui/BoosterUseOverlayView';
import type { BoosterType } from '../boosters/types';
import type { EasingFunction } from '../../libs/TweenManager/easing';
import { TopBarView } from './TopBarView';
import type { LevelTasksTracker } from '../tasks/LevelTasksTracker';
import { TaskScoreOverlay } from '../scoreOverlay/TaskScoreOverlay';
import { EventScheduler } from '../../utils/EventScheduler';
import type { TileModel } from '../boardMVC/models/board/TileModel';
import type { BlockerModel } from '../boardMVC/types';
import { startLevelEndAnimation } from './startLevelEndAnimation';
import { WinScreenBackgroundLayout } from '../../../layouts/winScreen/WinScreenBackgroundLayout';
import { getSafeAreaInset } from '../../safearea/SafeAreaProvider';
import type { EnemyBoardComponent } from '../../gameJam/EnemyBoardComponent';
import { VersusTopBarView } from '../../gameJam/VersusTopBarView';
import type { VersusTopBarViewParams } from '../../gameJam/VersusTopBarView';
import type { CollectionEventInGameTrayView } from '../../gameEvents/collectionEvent/views/CollectionEventInGameTrayView';

interface GameSceneViewDependencies {
  state: AppStateController;
  taskTracker: LevelTasksTracker;
  iapAvailable: boolean;
  versusMode: {
    enemyComponent: EnemyBoardComponent;
    versusTopBarParams: VersusTopBarViewParams;
  } | null;
}

export class GameSceneView extends Container {
  public readonly onScoreProgressDisplayed = new Signal<(points: number) => void>();

  public readonly onLastScoreProgressDisplayed = new Signal();

  public readonly onHelpButtonClicked = new Signal();

  private readonly scheduler = new EventScheduler();

  private readonly layout = new GameSceneLayout();

  private boosterPanel: Container | null = null;

  private boardView: BoardView | null = null;

  private readonly topBarView: TopBarView;

  private readonly scoreOverlay: TaskScoreOverlay;

  private readonly topBarContainerHeight = 140;

  private readonly boosterUseOverlay = new BoosterUseOverlayView().props({
    parent: this.layout.boosterUseOverlaySlot,
  });

  constructor(private readonly dependencies: GameSceneViewDependencies) {
    super();

    const { root, topBarSlot, helpButton } = this.layout;
    const { state, taskTracker, iapAvailable } = this.dependencies;

    root.props({
      parent: this,
    });

    this.interactive = true;
    this.hitArea = new Rectangle(0, 0, 1000000, 100000);

    state.subscriptions.onChange(
      ['horizontalLayout'],
      () => {
        const { width, height } = this.pixi.renderer;
        this.resize(width, height);
      },
      this,
    );
    this.on('destroyed', () => {
      state.subscriptions.removeAllSubscriptions(this);
    });

    if (dependencies.versusMode) {
      applyLayoutState(this.layout, 'versus');
      new VersusTopBarView(dependencies.versusMode.versusTopBarParams).props({
        parent: topBarSlot,
      });
    }

    this.topBarView = new TopBarView(state, taskTracker.tasks, iapAvailable).props({
      parent: topBarSlot,
      visible: !dependencies.versusMode,
    });
    if (dependencies.versusMode) {
      this.topBarView.pivot.x = 300;
    }

    helpButton.forEach((button) => {
      button.on('pointertap', () => {
        this.onHelpButtonClicked.emit();
      });
    });

    this.scoreOverlay = this.createTaskScoreOverlay();

    registerResizableObject(this);
  }

  private createTaskScoreOverlay(): TaskScoreOverlay {
    const scoreOverlay = new TaskScoreOverlay({
      scheduler: this.scheduler,
      tasksTracker: this.dependencies.taskTracker,
      getGlobalTargetPosition: (taskType): Point => this.topBarView.getGoalIconGlobalPos(taskType),
    }).props({
      parent: this.layout.scoreOverlayContainer,
    });

    let endAnimationPromise: Promise<void> | null = null;

    scoreOverlay.onFinalParticleAnimation.connect(async () => {
      if (!this.boardView) return;
      void this.togglePanels(false);
      endAnimationPromise = startLevelEndAnimation(this.boardView);
    });

    scoreOverlay.onTaskViewHit.connect(async (points, taskType) => {
      this.topBarView.onGoalHit(taskType);
      const progress = this.topBarView.incrementTaskProgress(taskType, points);
      if (progress) {
        this.onScoreProgressDisplayed.emit(progress.collected);
        if (this.topBarView.allTasksVisuallyComplete()) {
          if (endAnimationPromise) await endAnimationPromise;
          this.onLastScoreProgressDisplayed.emit();
        }
      }
    });

    return scoreOverlay;
  }

  public getTopBarView(): TopBarView {
    return this.topBarView;
  }

  public getOverlayContainer(): Container {
    return this.layout.overlayContainer;
  }

  public addGameAreaViews(gameAreaView: GameAreaView): this {
    const { traySlot, boardSlot } = this.layout;
    const { boardView, trayView } = gameAreaView;

    this.boardView = boardView.props({
      parent: boardSlot,
    });
    this.scoreOverlay.setBoardView(boardView);

    trayView.props({
      parent: traySlot,
    });

    this.alignBoard();

    return this;
  }

  public addBoosterPanel(boosterPanel: Container): this {
    this.boosterPanel = boosterPanel;
    this.resize();
    return this;
  }

  public resize(width: number = this.pixi.renderer.width, height: number = this.pixi.renderer.height): void {
    const { state, versusMode } = this.dependencies;
    const isHorizontal = state.isHorizontalLayoutActive();
    const {
      background,
      fullWidth,
      right,
      bottom,
      centerV,
      centerH,
      horizontalBoosterSlot,
      portraitBoosterSlot,
      topBarContainer,
      topBarSlot,
      collectionTraySlot,
    } = this.layout;
    const safeArea = getSafeAreaInset();

    if (isHorizontal) {
      applyLayoutState(this.layout, 'horizontal');
      this.boosterPanel?.props({ parent: horizontalBoosterSlot });
    } else {
      revokeLayoutState(this.layout, 'horizontal');
      this.boosterPanel?.props({ parent: portraitBoosterSlot });
    }

    if (versusMode) {
      applyLayoutState(this.layout, 'versus');
    }

    background.props({
      scale: Math.max(width / (background.width / background.scaleX), height / (background.height / background.scaleY)),
    });

    fullWidth.forEach((elem) => {
      elem.width = width;
    });
    bottom.forEach((elem) => {
      elem.y = height;
    });
    right.forEach((elem) => {
      elem.x = width;
    });
    centerH.forEach((elem) => {
      elem.x = width * 0.5;
    });
    centerV.forEach((elem) => {
      elem.y = height * 0.5;
    });

    topBarContainer.y = 0;
    topBarSlot.y = safeArea.top;
    topBarContainer.scaleY = 1 + safeArea.top / this.topBarContainerHeight;
    collectionTraySlot.pivot.y = -safeArea.top;

    this.alignBoard();
  }

  public async showBoosterUserOverlay(boosterType: BoosterType, onCancel: (() => void) | null): Promise<void> {
    const { boosterUseOverlay } = this;

    await this.togglePanels(false, 100);

    boosterUseOverlay.setBoosterUseTutorial(boosterType, onCancel);

    await boosterUseOverlay.show();
  }

  public async hideBoosterUserOverlay(): Promise<void> {
    const { boosterUseOverlay } = this;
    await boosterUseOverlay.hide();
    await this.togglePanels(true);
  }

  private togglePanels(visible: boolean, delay = 250): Promise<void> {
    const { bottomContainer, rightContainer } = this.layout;

    const ease: EasingFunction = visible
      ? // eslint-disable-next-line no-plusplus,no-param-reassign
        (k: number): number => --k * k * (2.1 * k + 1.1) + 1
      : (k: number): number => k * k * (2.1 * k - 1.1);
    const pivot = visible ? 0 : -500;

    bottomContainer.tweenTo({ pivotY: pivot }, delay, ease, true);
    return rightContainer.tweenTo({ pivotX: pivot }, delay, ease, true).asyncOnComplete();
  }

  private alignBoardPVP(enemyBoardView: BoardView, boardRT: Sprite): void {
    if (!this.boardView) return;
    const { state } = this.dependencies;

    const isHorizontal = state.isHorizontalLayoutActive();

    const horizontalMargin = 20;

    const { boardSlot, enemyBoardSlot } = this.layout;
    const maxScale = 1.5;

    const stageWidth = this.pixi.renderer.width;
    const stageHeight = this.pixi.renderer.height;

    const enemyBoardScale = 0.5;

    enemyBoardView.props({
      parent: enemyBoardSlot,
      scaleXY: enemyBoardScale,
      x: enemyBoardView.initialBounds.width * enemyBoardScale * -0.5,
    });

    if (isHorizontal) {
      boardSlot.y = stageHeight - 250;
    } else {
      boardSlot.y = stageHeight - (enemyBoardView ? 360 : 380);
    }

    const tilesOverflowPart = Math.max(
      0,
      Math.abs(this.boardView.initialBoundsWithTiles.minY) - Math.abs(this.boardView.initialBounds.minY) - 40,
    );

    const maxWidth = stageWidth - horizontalMargin * 2;
    let maxHeight = isHorizontal ? boardSlot.y - 300 : stageHeight - (600 + tilesOverflowPart);
    maxHeight -= enemyBoardView.initialBounds.height * enemyBoardScale * 0.8;

    const bounds = this.boardView.initialBounds;
    const scaleX = maxWidth / bounds.width;
    const scaleY = maxHeight / bounds.height;
    const scale = Math.min(scaleX, scaleY, maxScale);

    const x = bounds.width * scale * -0.5;
    const y = bounds.height * scale * -1;

    boardRT.props({
      x: enemyBoardView.x - enemyBoardView.scaleXY * (200 + enemyBoardView.initialBounds.x),
      y: enemyBoardView.y - enemyBoardView.scaleXY * (200 + enemyBoardView.initialBounds.y),
      scaleXY: enemyBoardView.scaleXY,
      parent: enemyBoardSlot,
      alpha: 0.6,
    });

    this.boardView.props({
      x,
      y,
      scale,
    });
  }

  private alignBoard(): void {
    if (!this.boardView) return;
    const { state, versusMode } = this.dependencies;

    if (versusMode?.enemyComponent) {
      this.alignBoardPVP(versusMode.enemyComponent.boardView, versusMode.enemyComponent.boardRTSprite);
      return;
    }

    const isHorizontal = state.isHorizontalLayoutActive();

    const horizontalMargin = 20;

    const { boardSlot } = this.layout;
    const maxScale = 1.5;

    const stageWidth = this.pixi.renderer.width;
    const stageHeight = this.pixi.renderer.height;

    if (isHorizontal) {
      boardSlot.y = stageHeight - 250;
    } else {
      boardSlot.y = stageHeight * 0.44;
    }

    const tilesOverflowPart = Math.max(
      0,
      Math.abs(this.boardView.initialBoundsWithTiles.minY) - Math.abs(this.boardView.initialBounds.minY) - 40,
    );

    const maxWidth = stageWidth - horizontalMargin * 2;
    const maxHeight = isHorizontal ? boardSlot.y - 300 : stageHeight - (600 + tilesOverflowPart);

    const bounds = this.boardView.initialBounds;
    const scaleX = maxWidth / bounds.width;
    const scaleY = maxHeight / bounds.height;
    const scale = Math.min(scaleX, scaleY, maxScale);

    const x = bounds.width * scale * -0.5;
    const y = bounds.height * scale * (isHorizontal ? -1 : -0.5);

    this.boardView.props({
      x,
      y,
      scale,
    });
  }

  public getBoardView(): BoardView | null {
    return this.boardView;
  }

  public fadeBoardArea(alpha: number): void {
    const { boardSlot, bottomContainer, rightContainer } = this.layout;
    [boardSlot, bottomContainer, rightContainer].forEach((container) => {
      container.tweenTo({ alpha }, 400, 'quadOut', true, 250);
    });
  }

  public async hideUI(): Promise<void> {
    this.topBarView?.resetCoinContainerParent();
    void this.togglePanels(false, 0);

    await Promise.all([
      this.layout.topBarContainer.tweenTo({ alpha: 0 }, 300, 'sinOut', true).asyncOnComplete(),
      this.layout.topBarSlot.tweenTo({ alpha: 0 }, 300, 'sinOut', true).asyncOnComplete(),
    ]);
  }

  public animateBoardProgress(tilesOrBlocker: TileModel[] | BlockerModel): Promise<void> {
    return this.scoreOverlay.animateProgress(tilesOrBlocker);
  }

  public fadeInWinScreenBackground(): void {
    const winScreenBackground = new WinScreenBackgroundLayout();

    const { root } = winScreenBackground;

    root.props({
      parent: this,
      alpha: 0,
    });

    root.tweenTo({ alpha: 1 }, 300, 'quadOut', true);
  }

  public getUnderTopBarSlot(): Container {
    return this.layout.underTopBarSlot;
  }

  public addCollectionEventTray(trayView: CollectionEventInGameTrayView): void {
    trayView.props({
      parent: this.layout.collectionTraySlot,
    });
  }

  public update(deltaTime: number): void {
    this.scheduler.update(deltaTime);
  }

  public destroy(): void {
    super.destroy({ children: true });
  }
}
